<div class="p-4">
  <div class="mb-4 flex items-center justify-between">
    <h3 class="!m-0 !font-bold">Bridge Devices</h3>
    <div class="flex gap-2">
      <pt-loader-button
        type="stroked"
        [loading]="downloading()"
        [disabled]="loading()"
        (clicked)="downloadDiagnosticReport()"
      >
        Download Diagnostics
      </pt-loader-button>
      <pt-loader-button
        type="stroked"
        [loading]="refreshingAll()"
        [disabled]="loading()"
        (clicked)="requestAllLogs()"
      >
        Request Updated Logs
      </pt-loader-button>
    </div>
  </div>

  @if (error(); as errorMessage) {
    <pr-information-box class="mb-4 block" theme="warn">
      {{ errorMessage }}
    </pr-information-box>
  }

  @if (successMessage(); as message) {
    <pr-information-box class="mb-4 block" theme="info">
      {{ message }}
    </pr-information-box>
  }

  @if (loading()) {
    <div
      class="flex flex-col items-center justify-center px-4 py-8 text-black opacity-60"
    >
      <mat-spinner diameter="40" />
      <p class="mt-4 text-sm">Loading device statuses...</p>
    </div>
  } @else if (devices().length === 0 && !error()) {
    <div
      class="flex flex-col items-center justify-center px-4 py-8 text-black opacity-40"
    >
      <mat-icon class="mb-4 !h-12 !w-12 !text-5xl">devices_off</mat-icon>
      <p class="m-0 text-sm">No bridge devices found.</p>
    </div>
  } @else {
    <table mat-table [dataSource]="devices()" class="w-full">
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef class="!w-16">Status</th>
        <td mat-cell *matCellDef="let device">
          <mat-icon
            class="!text-xl"
            [class]="getStatusColour(device.status)"
            [matTooltip]="getStatusLabel(device.status)"
            >{{ getStatusIcon(device.status) }}</mat-icon
          >
        </td>
      </ng-container>

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef>Device</th>
        <td mat-cell *matCellDef="let device">
          <div class="flex items-center gap-2 font-medium">
            {{ device.deviceName }}
            @if (device.deleted) {
              <span
                class="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700"
                >Deleted</span
              >
            }
          </div>
          <div class="text-xs text-black opacity-50">{{ device.deviceId }}</div>
        </td>
      </ng-container>

      <ng-container matColumnDef="version">
        <th mat-header-cell *matHeaderCellDef>Version</th>
        <td mat-cell *matCellDef="let device">
          <span class="text-sm" [ngClass]="{ 'opacity-30': !device.version }">{{
            device.version ?? '—'
          }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="logs">
        <th mat-header-cell *matHeaderCellDef class="!w-16">Logs</th>
        <td mat-cell *matCellDef="let device">
          <mat-icon
            class="!text-xl {{ getLogColour(device.logFreshness) }}"
            [matTooltip]="getLogTooltip(device)"
            >{{ getLogIcon(device.logFreshness) }}</mat-icon
          >
        </td>
      </ng-container>

      <ng-container matColumnDef="latestLog">
        <th mat-header-cell *matHeaderCellDef>Latest Log</th>
        <td mat-cell *matCellDef="let device">
          <span
            class="text-sm"
            [ngClass]="{
              'text-black opacity-30': !device.latestLogDate
            }"
            [matTooltip]="device.latestLogDate?.toLocaleString() ?? ''"
            >{{ device.latestLogDate | moment | amTimeAgo }}</span
          >
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="!w-12"></th>
        <td mat-cell *matCellDef="let device">
          @if (requestingLogs().has(device.deviceId)) {
            <mat-spinner diameter="20" />
          } @else {
            <button
              mat-icon-button
              [matMenuTriggerFor]="deviceMenu"
              [matMenuTriggerData]="{ device: device }"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
          }
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
    </table>

    <mat-menu #deviceMenu="matMenu">
      <ng-template matMenuContent let-device="device">
        <button
          mat-menu-item
          (click)="requestLogs(device)"
          [disabled]="!device.deviceId"
        >
          <mat-icon>refresh</mat-icon>
          <span>Request Updated Logs</span>
        </button>
        <button mat-menu-item (click)="viewLogs(device)">
          <mat-icon>description</mat-icon>
          <span>View Logs</span>
        </button>
        <mat-divider />
        <button mat-menu-item (click)="editDeviceName(device)">
          <mat-icon>edit</mat-icon>
          <span>Edit Name</span>
        </button>
        <button
          mat-menu-item
          (click)="updateSettings(device)"
          [disabled]="!device.hasSettings"
        >
          <mat-icon>settings</mat-icon>
          <span>Update Settings</span>
        </button>
        <button
          mat-menu-item
          (click)="generateIntegrationKey(device)"
          [disabled]="!device.deviceId"
        >
          <mat-icon>vpn_key</mat-icon>
          <span>{{
            device.hasIntegrationKey
              ? 'Regenerate Access Key'
              : 'Generate Access Key'
          }}</span>
        </button>
        <button
          mat-menu-item
          (click)="generateDownloadLink(device)"
          [disabled]="!device.deviceId"
        >
          <mat-icon>link</mat-icon>
          <span>Generate Download Link</span>
        </button>
        <mat-divider />
        <button
          mat-menu-item
          (click)="copyDeviceId(device)"
          [disabled]="!device.deviceId"
        >
          <mat-icon>content_copy</mat-icon>
          <span>Copy Device ID</span>
        </button>
        <button
          mat-menu-item
          (click)="downloadBridge(device)"
          [disabled]="!device.downloadUrl"
        >
          <mat-icon>cloud_download</mat-icon>
          <span>Download Bridge</span>
        </button>
        <mat-divider />
        <button
          mat-menu-item
          (click)="deleteDevice(device)"
          class="!text-red-600"
        >
          <mat-icon class="!text-red-600">delete</mat-icon>
          <span>Delete Device</span>
        </button>
      </ng-template>
    </mat-menu>
  }
</div>
